<script setup lang="ts">
import { RunUpdateUpgradeRequestDto } from '@/api/generator';
import logo from '@/assets/images/logo.png';
import Button from '@/components/buttons/Button.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { formBackendErrorHandler } from '@/errorHandling';
import { useAppUpdate } from '@/utils/useAppUpdate';
import { useLoading } from '@/utils/useLoading';
import { Step, useSteps } from '@/utils/useSteps';
import { useTranslation } from 'i18next-vue';
import { computed, nextTick, useTemplateRef, watch, watchEffect } from 'vue';
import { VBtn } from 'vuetify/components';
import WizardStepInformation from './WizardStepInformation.vue';
import WizardStepSecurityCheck from './WizardStepSecurityCheck.vue';
import WizardStepUpdateStatus from './WizardStepUpdateStatus.vue';
import WizardStepWelcome from './WizardStepWelcome.vue';

const { t } = useTranslation();

export type UpdateType = 'update' | 'upgrade';

export type UpdateWizardProps = {
  modelValue: boolean;
  type: UpdateType;
  showStatus: boolean;
};

const { type, showStatus } = defineProps<UpdateWizardProps>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const update = computed(() => available?.value[type]);

const { form } = useVeeForm<RunUpdateUpgradeRequestDto>({
  schemaName: 'RunUpdateUpgradeRequestDto',
  mode: 'add',
  options: {
    keepValuesOnUnmount: true, // keepValuesOnUnmount because configuration steps conditionally render and unmount between steps
  },
});
const { values, handleSubmit, resetForm } = form;

const {
  available,
  installedVersion,
  startUpdate,
  openProgress,
  clearNotification,
  exceptionNotification,
  notificationMessage,
  loggedInUsers,
} = useAppUpdate(t, formBackendErrorHandler(form, true));

// Using watch + resetForm instead of watchEffect + setValues
// This prevents vee-validate dirty/valid state issues on initial render
watch(
  () => update.value?.version,
  () => {
    resetForm({ values: { targetVersion: update.value?.version || undefined } });
  },
);

const title = computed(() => {
  switch (type) {
    case 'update':
      return `${t('Update')} ${t('UnicheckSoftware')}`;
    case 'upgrade':
      return `${t('Upgrade')} ${t('UnicheckSoftware')}`;
    default:
      throw new Error('Update type unknown');
  }
});

const subtitle = computed(() => {
  const key = type === 'update' ? 'UpdateFromVersionToVersion' : 'UpgradeFromVersionToVersion';

  return installedVersion.value && update.value?.version
    ? t(key, {
        installedVersion: installedVersion.value,
        targetVersion: update.value?.version,
      })
    : undefined;
});

const backupConfirmed = computed(() => !!values.backupConfirmed);

const wizardSteps = computed<Step[]>(() => [
  {
    id: 'welcome',
  },
  {
    id: 'information',
  },
  {
    id: 'securityCheck',
    available: backupConfirmed.value,
  },
  {
    id: 'updateStatus',
  },
]);

const { inStep, inLastStep, inFirstStep, back, next, nextAllowed, backAllowed, reset, status } =
  useSteps(wizardSteps);

const nextButton = useTemplateRef<InstanceType<typeof VBtn>>('nextButton');

watchEffect(() => {
  if (showStatus) {
    status();
  }
  if (inFirstStep.value) {
    focusNext();
  }
});

async function focusNext() {
  await nextTick();
  nextButton.value?.$el.focus();
}

function closeWizard() {
  resetForm({ values: undefined });
  clearNotification();
  emit('update:modelValue', false);
  reset();
}

const { action: runUpdate, loading: startPending } = useLoading(
  handleSubmit(async (runUpdateUpgradeRequestDto) => {
    const isStarted = await startUpdate(runUpdateUpgradeRequestDto, type);

    if (isStarted) {
      openProgress(type);
    }
  }),
);
</script>

<template>
  <Dialog
    :modelValue="modelValue"
    minWidth="800"
    maxWidth="800"
    minHeight="800"
    transition="slide-x-reverse-transition"
    @update:modelValue="(open) => !open && closeWizard()"
  >
    <template #title>
      <div class="d-flex justify-start align-center">
        <v-img width="130" class="mr-6" alt="PTW company logo" :src="logo" />

        <div class="mt-2">
          <h5>{{ title }}</h5>
          <h6 v-if="subtitle" class="text-body-2">{{ subtitle }}</h6>
        </div>
      </div>
    </template>

    <template #text>
      <WizardStepWelcome
        v-if="inStep('welcome')"
        :type="type"
        :targetVersion="update?.version || ''"
      />

      <WizardStepInformation v-if="inStep('information')" :type="type" @confirmed="focusNext" />

      <WizardStepSecurityCheck
        v-if="inStep('securityCheck')"
        :type="type"
        :exceptionNotification="exceptionNotification"
        :notificationMessage="notificationMessage"
        :loggedInUsers="loggedInUsers ? [...loggedInUsers] : undefined"
        @submit="runUpdate()"
      />

      <WizardStepUpdateStatus v-if="inStep('updateStatus')" :form="form" :type="type" />
    </template>
    <template #actions>
      <Button
        v-if="!inStep('updateStatus')"
        variant="text"
        :disabled="startPending"
        class="mr-0"
        @click="closeWizard()"
      >
        {{ $t('Cancel') }}
      </Button>

      <Button
        v-if="!inFirstStep && !inStep('updateStatus')"
        class="mr-0"
        :disabled="!backAllowed || startPending"
        variant="text"
        @click="
          clearNotification();
          back();
        "
      >
        &#9204; {{ $t('Back') }}
      </Button>

      <Button
        v-if="!inLastStep && !inStep('securityCheck')"
        ref="nextButton"
        variant="secondary"
        :disabled="!nextAllowed"
        @click="
          clearNotification();
          next();
        "
      >
        {{ $t('Next') }}
      </Button>

      <VeeSubmitButton
        v-if="inStep('securityCheck')"
        :loading="startPending"
        :text="$t(type === 'update' ? 'StartUpdate' : 'StartUpgrade')"
        @click="runUpdate()"
      />

      <Button v-if="inStep('updateStatus')" variant="primary" @click="closeWizard()">
        {{ $t('Close') }}
      </Button>
    </template>
  </Dialog>
</template>
